import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const CreateConference = () => {
  const { user, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    plan: 'basic',
    startDate: '',
    endDate: '',
  });
  
  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-white">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }
  
  // Redirect unauthenticated users
  if (!isAuthenticated) {
    navigate('/auth');
    return null;
  }
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Fake payment wall - in real implementation, this would connect to Stripe
      // For now, we'll just create the conference in the database without charging
      
      const { data, error } = await supabase
        .from('conferences')
        .insert([
          {
            name: formData.name,
            description: formData.description,
            plan: formData.plan,
            start_date: formData.startDate,
            end_date: formData.endDate,
            owner_id: user?.id,
            active: true,
          }
        ])
        .select()
        .single();
      
      if (error) throw error;
      
      toast({
        title: "Success!",
        description: "Your conference has been created.",
      });
      
      // Navigate to the conference admin page
      navigate(`/admin?conference=${data.id}`);
    } catch (error: any) {
      console.error('Error creating conference:', error);
      toast({
        title: "Failed to create conference",
        description: error.message || "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-b from-black to-indigo-950">
      <Header />
      
      <main className="flex-grow pt-28 pb-20">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-8 text-center">Create Your Conference</h1>
          
          <Card className="bg-white/5 backdrop-blur-sm border-purple-500/20">
            <CardHeader>
              <CardTitle className="text-2xl text-white">Conference Details</CardTitle>
              <CardDescription className="text-gray-300">
                Fill out the information about your conference
              </CardDescription>
            </CardHeader>
            
            <form onSubmit={handleSubmit}>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="name" className="text-sm font-medium text-white">
                    Conference Name
                  </label>
                  <Input
                    id="name"
                    name="name"
                    placeholder="AI Summit 2025"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="bg-white/10 border-white/20 text-white"
                  />
                </div>
                
                <div className="space-y-2">
                  <label htmlFor="description" className="text-sm font-medium text-white">
                    Description
                  </label>
                  <Textarea
                    id="description"
                    name="description"
                    placeholder="A conference about the latest AI technologies..."
                    value={formData.description}
                    onChange={handleChange}
                    required
                    className="bg-white/10 border-white/20 text-white min-h-[100px]"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label htmlFor="startDate" className="text-sm font-medium text-white">
                      Start Date
                    </label>
                    <Input
                      id="startDate"
                      name="startDate"
                      type="date"
                      value={formData.startDate}
                      onChange={handleChange}
                      required
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label htmlFor="endDate" className="text-sm font-medium text-white">
                      End Date
                    </label>
                    <Input
                      id="endDate"
                      name="endDate"
                      type="date"
                      value={formData.endDate}
                      onChange={handleChange}
                      required
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label htmlFor="plan" className="text-sm font-medium text-white">
                    Subscription Plan
                  </label>
                  <Select
                    value={formData.plan}
                    onValueChange={(value) => handleSelectChange('plan', value)}
                  >
                    <SelectTrigger className="bg-white/10 border-white/20 text-white">
                      <SelectValue placeholder="Select a plan" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="basic">Basic ($99/month)</SelectItem>
                      <SelectItem value="pro">Professional ($199/month)</SelectItem>
                      <SelectItem value="enterprise">Enterprise ($499/month)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
              
              <CardFooter className="flex flex-col space-y-4">
                <Button 
                  type="submit" 
                  className="w-full bg-purple-600 hover:bg-purple-700"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Creating..." : "Create Conference"}
                </Button>
                
                <div className="text-xs text-gray-400 text-center">
                  By creating a conference, you agree to our Terms of Service and Privacy Policy.
                  <br />
                  Your subscription will begin immediately. (This is a demo - no actual charges will be made)
                </div>
              </CardFooter>
            </form>
          </Card>
          
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
            <PlanCard 
              title="Basic"
              price="$99"
              features={[
                "Up to 500 attendees",
                "Basic branding options",
                "Speaker management",
                "Schedule builder",
                "Email support"
              ]}
              recommended={false}
            />
            
            <PlanCard 
              title="Professional"
              price="$199"
              features={[
                "Up to 2,000 attendees",
                "Advanced branding options",
                "Speaker management",
                "Schedule builder",
                "Ticket sales",
                "Custom domain",
                "Priority support"
              ]}
              recommended={true}
            />
            
            <PlanCard 
              title="Enterprise"
              price="$499"
              features={[
                "Unlimited attendees",
                "Full customization",
                "Speaker management",
                "Schedule builder",
                "Ticket sales",
                "Custom domain",
                "API access",
                "24/7 dedicated support",
                "Analytics dashboard"
              ]}
              recommended={false}
            />
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

// Component for displaying plan pricing cards
const PlanCard = ({ 
  title, 
  price, 
  features, 
  recommended 
}: { 
  title: string; 
  price: string; 
  features: string[]; 
  recommended: boolean; 
}) => {
  return (
    <div className={`rounded-lg overflow-hidden border ${
      recommended ? 'border-purple-500 transform scale-105' : 'border-white/10'
    } bg-white/5 backdrop-blur-sm p-6 flex flex-col relative`}>
      {recommended && (
        <div className="absolute top-0 right-0">
          <div className="bg-purple-600 text-white text-xs px-4 py-1 rounded-bl-lg">
            Recommended
          </div>
        </div>
      )}
      
      <h3 className="text-xl font-bold text-white">{title}</h3>
      <div className="mt-2 mb-4">
        <span className="text-3xl font-bold text-white">{price}</span>
        <span className="text-gray-300 ml-1">/month</span>
      </div>
      
      <ul className="mt-4 mb-6 flex-grow space-y-2">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start text-gray-300">
            <svg className="h-5 w-5 text-purple-400 mr-2 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            {feature}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default CreateConference;
